// Opens the singleton confirmation dialog in app/views/shared/_confirm_modal.html.erb and resolves
// to whether the person confirmed. Extracted from confirm_modal_controller so the controller and
// the delivery recipient guard share one dialog instead of each hand-rolling its own; the elements
// it reaches for are all in that partial. See docs/ui_components.md.
//
// Options: message (a string, or [{text, bold}] segments appended as text nodes, never as markup --
// what's in them is data), title, confirmLabel, warning (shows the icon), allowSkip + confirmKey
// ("don't ask me this again", remembered in localStorage), and note ({text, linkText, href}) for a
// line below the buttons pointing at wherever the prompt can be corrected.

const MODAL_ID = "confirm-modal-singleton";
const skipKey = (confirmKey) => `confirm_skip_${confirmKey}`;

// One dialog for the whole page means only one prompt can own its buttons. Anything still open when
// a new prompt arrives is answered "no" first -- otherwise both listen on the same Confirm button
// and a single click resolves them both, which on the delivery surfaces means two sends, to two
// different recipients, from one confirmation.
let dismissOpenDialog = null;

// Storage is unavailable in some private-browsing modes. Failing to read means asking again, and
// failing to write means asking again -- both the safe direction.
export function confirmSkipped(confirmKey) {
  try {
    return !!confirmKey && window.localStorage.getItem(skipKey(confirmKey)) === "true";
  } catch {
    return false;
  }
}

function rememberSkip(confirmKey) {
  try {
    if (confirmKey) window.localStorage.setItem(skipKey(confirmKey), "true");
  } catch {
    // Nothing to do.
  }
}

function renderMessage(element, message) {
  if (!Array.isArray(message)) {
    element.textContent = message;
    return;
  }

  element.replaceChildren(
    ...message.map(({ text, bold }) => {
      const node = document.createElement(bold ? "strong" : "span");
      node.textContent = text;
      return node;
    })
  );
}

export async function showConfirmModal({
  message,
  title = null,
  confirmLabel = "Confirm",
  warning = false,
  allowSkip = false,
  confirmKey = null,
  note = null,
} = {}) {
  const modal = document.getElementById(MODAL_ID);
  // Rendered by the v2 layout; on a page without it, still ask rather than silently proceeding.
  if (!modal) return window.confirm(Array.isArray(message) ? message.map((s) => s.text).join("") : message);

  const el = (name) => modal.querySelector(`[data-confirm-modal-target="${name}"]`);
  const button = (action) => modal.querySelector(`[data-action="click->confirm-modal#${action}"]`);
  const skipCheckbox = el("dontAskAgain");
  const confirmBtn = button("confirm");
  const cancelBtn = button("cancel");

  // Every optional piece is set on both branches: the dialog is shared, so anything left over from
  // a previous open would surface on the next, unrelated one.
  renderMessage(el("message"), message);
  el("title").textContent = title || "";
  el("title").classList.toggle("hidden", !title);
  el("icon").classList.toggle("hidden", !warning);
  el("confirmLabel").textContent = confirmLabel;
  el("dontAskAgainContainer").classList.toggle("hidden", !allowSkip);
  skipCheckbox.checked = false;
  el("note").classList.toggle("hidden", !note);
  el("noteText").textContent = note?.text ? `${note.text} ` : "";
  el("noteLink").textContent = note?.linkText || "";
  el("noteLink").href = note?.href || "#";

  if (dismissOpenDialog) dismissOpenDialog();
  modal.classList.remove("hidden");

  return new Promise((resolve) => {
    const settle = (answer) => {
      confirmBtn.removeEventListener("click", onConfirm);
      cancelBtn.removeEventListener("click", onCancel);
      if (dismissOpenDialog === onCancel) dismissOpenDialog = null;
      modal.classList.add("hidden");
      resolve(answer);
    };

    const onConfirm = () => {
      if (allowSkip && skipCheckbox.checked) rememberSkip(confirmKey);
      settle(true);
    };
    const onCancel = () => settle(false);

    confirmBtn.addEventListener("click", onConfirm);
    cancelBtn.addEventListener("click", onCancel);
    dismissOpenDialog = onCancel;
  });
}
